import {
  EmptyState,
  FlexColumn,
  FlexRow,
  Loading,
  MaterialIcon,
  StyledSearchBar,
  useDebouncedSearch,
  useModal,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { useEffect, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useStyles } from '../../hooks/useStyles';
import {
  formsIdsState,
  formsPageState,
  formsState,
  loadingFormsState,
  searchFormsState,
  selectedFormIdState,
} from '../../storage/formStates';
import { FloatingCardContent } from '../layout/cards/FloatingCardContent';
import StyledTooltip from '../ui/StyledTooltip';
import { FormListItem } from './FormListItem';
import { NewFormModal } from './NewFormModal';

export function FormList() {
  const { theme, classes, css } = useStyles();
  const formIds = useRecoilValue(formsIdsState);
  const loadingForms = useRecoilValue(loadingFormsState);
  const { search, setSearch } = useDebouncedSearch(useRecoilState(searchFormsState));
  const paginatedForms = useRecoilValue(formsState);
  const [formsPage, setFormsPage] = useRecoilState(formsPageState);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const setSelectedForm = useSetRecoilState(selectedFormIdState);
  const { openModal, closeModal, showModal } = useModal();

  const onUpdate = () => {
    setFormsPage(0);
    setSelectedForm(undefined);
  };

  useEffect(() => {
    if (!scrollableRef.current) return;
    if (formsPage === 0) {
      const scrollableDiv = scrollableRef.current;
      scrollableDiv.scrollTo(0, 0);
    }
  }, [formsPage, scrollableRef]);

  return (
    <FloatingCardContent
      flexible
      gap={theme.sizing.scale500}
    >
      {!formIds && !loadingForms && <EmptyState title="No cuentas con ningún usuario." />}
      {!formIds && loadingForms && <Loading />}
      {formIds && (
        <>
          <FlexRow gap={theme.sizing.scale300}>
            <div className={classes.flexible(1)}>
              <StyledSearchBar
                name="form-search"
                value={search}
                onChange={setSearch}
                clearable
                noMargin
              />
            </div>
            <FlexRow
              gap={0}
              classNames={css({ alignItems: 'center' })}
            >
              <StyledTooltip
                content={'Nuevo'}
                showArrow={false}
              >
                <Button
                  size="mini"
                  shape="square"
                  onClick={openModal}
                  kind="tertiary"
                >
                  <MaterialIcon name={'add'} />
                </Button>
              </StyledTooltip>
              <StyledTooltip
                content={'Actualizar'}
                showArrow={false}
              >
                <Button
                  size="mini"
                  shape="square"
                  onClick={onUpdate}
                  kind="tertiary"
                >
                  <MaterialIcon name={'sync'} />
                </Button>
              </StyledTooltip>
            </FlexRow>
          </FlexRow>
          {!!paginatedForms?.items.length && (
            <div
              id="scrollableDiv"
              className={css({ height: '100%', overflow: 'auto', marginBottom: '12px' })}
              ref={scrollableRef}
            >
              <InfiniteScroll
                dataLength={paginatedForms?.items.length}
                next={() => setFormsPage(formsPage + 1)}
                hasMore={paginatedForms?.hasNext ?? false}
                loader={
                  <div className={css({ height: '80px', overflow: 'hidden' })}>
                    <Loading />
                  </div>
                }
                endMessage={
                  paginatedForms?.hasNext && (
                    <p style={{ textAlign: 'center' }}>
                      <b>Cargando...</b>
                    </p>
                  )
                }
                scrollableTarget="scrollableDiv"
              >
                <FlexColumn gap={0}>
                  {formIds.map((formId) => (
                    <FormListItem
                      key={formId}
                      formId={formId}
                    />
                  ))}
                </FlexColumn>
              </InfiniteScroll>
            </div>
          )}
          {!formIds.length && (
            <EmptyState
              title="No se encontraron resultados"
              description="Intenta con otra busqueda"
            />
          )}
        </>
      )}
      <NewFormModal
        isOpen={showModal}
        onClose={closeModal}
      />
    </FloatingCardContent>
  );
}
